import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ApiEnvelope,
  buildTrendRequest,
  TrendApiError,
  TrendRequest,
  TrendResponseData,
  TrendTimeframeGrain,
} from '../services/completedTrendApi';
import type { AppliedFiltersSnapshot } from '../OnTimeDispatchDashboard';

const DEFAULT_TIMEOUT_MS = 65000; // slightly above Apigee 60s gateway timeout
const DEBOUNCE_MS = 100;

export interface UseTrendReturn {
  data: TrendResponseData | null;
  loading: boolean;
  error: string | null;
  noData: boolean;
  message: string | null;
  refetch: () => void;
}

type TrendFetcher = (
  payload: TrendRequest,
  options?: { timeoutMs?: number; signal?: AbortSignal }
) => Promise<ApiEnvelope<TrendResponseData>>;

// Shared by every trend chart (Completed % Trend, On-Time % Trend, ...) — only the fetch fn/label differ.
export function useTrend(
  fetchTrend: TrendFetcher,
  metricLabel: string,
  appliedFilters: AppliedFiltersSnapshot,
  timeframeGrain: TrendTimeframeGrain
): UseTrendReturn {
  const [data, setData] = useState<TrendResponseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [triggerCount, setTriggerCount] = useState(0);

  const requestIdRef = useRef(0);
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  const refetch = useCallback(() => {
    setTriggerCount((prev) => prev + 1);
  }, []);

  const requestPayload = useMemo(
    () => buildTrendRequest(appliedFilters, timeframeGrain),
    [appliedFilters, timeframeGrain]
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      abortControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setError(null);

    const timer = setTimeout(async () => {
      try {
        const response = await fetchTrend(requestPayload, {
          timeoutMs: DEFAULT_TIMEOUT_MS,
          signal: controller.signal,
        });

        if (!isMountedRef.current || requestIdRef.current !== requestId) return;
        setData(response.data);
      } catch (err: any) {
        if (!isMountedRef.current || requestIdRef.current !== requestId) return;
        if (controller.signal.aborted || err?.name === 'CanceledError' || err?.name === 'AbortError') return;

        const message =
          err instanceof TrendApiError || err instanceof Error
            ? err.message
            : `Failed to load ${metricLabel} data`;
        setData(null);
        setError(message);
      } finally {
        if (isMountedRef.current && requestIdRef.current === requestId) {
          setLoading(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [requestPayload, triggerCount, fetchTrend, metricLabel]);

  return {
    data,
    loading,
    error,
    noData: !!data?.noData,
    message: data?.message ?? null,
    refetch,
  };
}
